/**
 * PlaysHub — public, indexable basketball plays page.
 *
 * SEO strategy
 * ────────────
 * - Static-feeling URL: `/jugadas` and `/jugadas/:slug` (Spanish for organic
 *   discovery; English/French readers still see translated copy).
 * - `<Helmet>` tags injected per-route so Googlebot + social scrapers grab
 *   real metadata. JSON-LD `ItemList` schema makes the listing eligible for
 *   rich results.
 * - The listing always sends a `view` ping to `/api/playshub/plays/:slug/track`
 *   for *local* analytics — the same data feeds the Admin dashboard.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { Heart, MessageSquare, Eye, Sparkles, ChevronDown, Globe } from 'lucide-react';
import { useLang, ACTIVE_LANGUAGES as LANGUAGES } from '@/context/LangContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CATS = ['all', 'man', 'zone', 'ato', 'blob', 'slob', 'endgame', 'set', 'transition'];
const SORTS = ['recent', 'popular', 'trending'];

// URL aliases per language — we expose 3 canonical URLs that all render this
// same component. hreflang annotations point Google at the right localized
// URL for each locale.
const LANG_PATH = { es: '/jugadas', en: '/plays', fr: '/jeux' };
const OG_LOCALE = { es: 'es_ES', en: 'en_US', fr: 'fr_FR' };
const LANG_LOCALE = { es: 'es-ES', en: 'en-US', fr: 'fr-FR' };

const fmtNum = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return `${(v / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(v);
};

// Image URLs uploaded via /api/playshub/admin/upload-image come back as
// site-relative paths so they survive a hostname change. The browser
// resolves them against the current origin (preview vs prod), but social
// scrapers (OG/Twitter) need absolute URLs — we prepend backend host then.
const absImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${API}${url}`;
  return url;
};

function Counter({ total, t }) {
  return (
    <div className="flex items-baseline gap-3" data-testid="playshub-counter">
      <div className="text-[10px] font-mono uppercase tracking-[0.4em] text-gold/80">{t('ph_counter_label')}</div>
      <div className="font-h text-5xl sm:text-6xl tabular-nums text-white">
        {total}<span className="text-gold">+</span>
      </div>
      <div className="text-sm font-mono uppercase tracking-widest text-slate-400">{t('ph_counter_word')}</div>
    </div>
  );
}

function PlayCard({ play, lang }) {
  return (
    <Link
      to={`/jugadas/${play.slug}`}
      data-testid={`playshub-card-${play.slug}`}
      className="group relative block rounded-xl border border-gold/20 bg-navy-deep overflow-hidden hover:border-gold/60 transition-all"
    >
      {/* Image (or placeholder with category text). The image is server-cropped
          to 60-70% to protect the full play visual behind the paywall. */}
      <div className="relative aspect-[16/10] bg-gradient-to-br from-navy to-navy-deep">
        {play.image_partial_url ? (
          <img
            src={absImg(play.image_partial_url)}
            alt={play.title}
            loading="lazy"
            className="w-full h-full object-cover opacity-90"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-gold/40 font-h text-4xl uppercase tracking-widest">
              {play.category || 'set'}
            </div>
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-navy-deep to-transparent" />
        <div className="absolute top-2 right-2 px-2 py-1 rounded-md bg-black/60 backdrop-blur text-[10px] font-mono uppercase tracking-widest text-gold border border-gold/30">
          {play.category}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-h text-lg leading-tight text-white group-hover:text-gold transition-colors">
          {play.title}
        </h3>
        <p className="mt-1 text-xs text-slate-400 line-clamp-2 min-h-[2.4em]">
          {play.summary_partial}
        </p>
        <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3 text-rose-400" /> {fmtNum(play.likes_count)}</span>
            <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3 text-sky-400" /> {fmtNum(play.comments_count)}</span>
            <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3 text-slate-300" /> {fmtNum(play.views_count)}</span>
          </div>
          <span className="text-gold/80 font-mono uppercase tracking-widest text-[10px]">
            {(play.tags || []).slice(0, 2).join(' · ')}
          </span>
        </div>
      </div>
      {/* Hover CTA — appears at the bottom for affordance */}
      <div className="absolute inset-x-0 bottom-0 px-4 py-2 bg-gold text-navy-deep font-mono uppercase tracking-widest text-[10px] text-center translate-y-full group-hover:translate-y-0 transition-transform">
        Ver jugada →
      </div>
    </Link>
  );
}

export default function PlaysHub() {
  const { lang, setLang, t } = useLang();
  const [plays, setPlays] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [skip, setSkip] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [counter, setCounter] = React.useState(0);
  const [yoloShots, setYoloShots] = React.useState(0);
  const [cat, setCat] = React.useState('all');
  const [sort, setSort] = React.useState('recent');
  const limit = 24;

  const fetchPlays = React.useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const params = { limit, skip: reset ? 0 : skip, sort };
      if (cat !== 'all') params.category = cat;
      const r = await axios.get(`${API}/api/playshub/plays`, { params });
      const next = r.data?.plays || [];
      setTotal(r.data?.total || 0);
      setPlays((prev) => (reset ? next : [...prev, ...next]));
      if (reset) setSkip(next.length); else setSkip((s) => s + next.length);
    } finally {
      setLoading(false);
    }
  }, [skip, cat, sort]);

  // Initial load + on filter/sort change
  React.useEffect(() => {
    fetchPlays(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cat, sort]);

  // Counter (cached on backend, refresh every 30s if the tab stays open)
  React.useEffect(() => {
    let alive = true;
    const fetchCounter = () => {
      axios.get(`${API}/api/playshub/counter`).then((r) => {
        if (alive) setCounter(r.data?.total || 0);
      }).catch(() => {});
    };
    fetchCounter();
    const id = setInterval(fetchCounter, 30000);
    // Public YOLO training counter — refreshed once on mount.
    axios.get(`${API}/api/yolo/stats/public`).then((r) => {
      if (alive) setYoloShots(r.data?.total_shots || 0);
    }).catch(() => {});
    return () => { alive = false; clearInterval(id); };
  }, []);

  const itemListLd = React.useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: plays.slice(0, 20).map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `https://playmasteria.com/jugadas/${p.slug}`,
      name: p.title,
    })),
  }), [plays]);

  return (
    <div className="min-h-screen bg-navy text-white">
      <Helmet>
        <title>{`${t('ph_nav_jugadas')} · PlayMaster IA`}</title>
        <meta name="description" content={t('ph_tagline')} />
        <link rel="canonical" href={`https://playmasteria.com${LANG_PATH[lang] || '/jugadas'}`} />
        <link rel="alternate" hrefLang="es" href="https://playmasteria.com/jugadas" />
        <link rel="alternate" hrefLang="en" href="https://playmasteria.com/plays" />
        <link rel="alternate" hrefLang="fr" href="https://playmasteria.com/jeux" />
        <link rel="alternate" hrefLang="x-default" href="https://playmasteria.com/jugadas" />
        <meta property="og:title" content={`${t('ph_nav_jugadas')} · PlayMaster IA`} />
        <meta property="og:description" content={t('ph_tagline')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://playmasteria.com${LANG_PATH[lang] || '/jugadas'}`} />
        <meta property="og:locale" content={OG_LOCALE[lang] || 'es_ES'} />
        <script type="application/ld+json">{JSON.stringify(itemListLd)}</script>
      </Helmet>

      {/* Public header — minimal, links back to landing + login */}
      <header className="sticky top-0 z-40 bg-navy-deep/95 backdrop-blur border-b border-gold/20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="PlayMaster IA" className="w-12 h-12 sm:w-14 sm:h-14" />
            <span className="font-h text-lg sm:text-xl uppercase tracking-widest">PlayMaster IA</span>
          </Link>
          <nav className="flex items-center gap-3 text-xs font-mono uppercase tracking-widest">
            <Link to="/blog" className="text-slate-300 hover:text-gold">{t('ph_nav_blog')}</Link>
            <Link to="/login" className="text-slate-300 hover:text-gold" data-testid="playshub-nav-login">{t('ph_nav_login')}</Link>
            <Link to="/register" className="px-3 py-1.5 rounded-md bg-gold text-navy-deep hover:bg-gold/90" data-testid="playshub-nav-register">
              {t('ph_nav_signup')}
            </Link>
            {/* Public language selector — lets Google-arriving visitors switch
                between ES / EN / FR without needing to log in. */}
            <div className="relative" data-testid="playshub-lang-selector">
              <select
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                className="appearance-none bg-navy border border-gold/30 rounded-md pl-7 pr-6 py-1.5 text-[11px] font-mono uppercase tracking-widest text-gold hover:border-gold/60 cursor-pointer"
                aria-label="Language"
              >
                {LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>{l.flag}</option>
                ))}
              </select>
              <Globe className="absolute left-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gold pointer-events-none" />
            </div>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10">
        {/* Hero — counter + tagline */}
        <section className="mb-10">
          <Counter total={counter} t={t} />
          <p className="mt-4 text-base text-slate-300 max-w-2xl">
            {t('ph_tagline')}
          </p>
          {yoloShots > 0 && (
            <div
              data-testid="playshub-training-pill"
              className="mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 pl-3 pr-4 py-1.5 text-[11px] font-mono uppercase tracking-widest text-emerald-300"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              {t('ph_training_pill_prefix')} <span className="tabular-nums font-bold text-emerald-200">{yoloShots.toLocaleString(LANG_LOCALE[lang] || 'es-ES')}</span> {t('ph_training_pill_suffix')}
            </div>
          )}

          {/* Registration CTA — converts SEO visitors into accounts. The full
              play breakdown (`summary_full`, step-by-step animations) is gated
              behind login, so we tell the visitor why creating an account is
              worth it. */}
          <div
            data-testid="playshub-register-cta"
            className="mt-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-xl border border-gold/40 bg-gradient-to-r from-gold/10 to-amber-500/5 px-5 py-4"
          >
            <Sparkles className="w-6 h-6 text-gold shrink-0" />
            <div className="flex-1">
              <h2 className="font-h text-lg sm:text-xl text-gold uppercase tracking-tight">{t('ph_cta_title')}</h2>
              <p className="text-sm text-slate-300 mt-1">{t('ph_cta_subtitle')}</p>
            </div>
            <Link
              to="/register"
              data-testid="playshub-cta-register-btn"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold text-navy-deep font-h uppercase tracking-wider text-sm hover:bg-gold/90 transition-colors"
            >
              {t('ph_cta_button')}
            </Link>
          </div>
        </section>

        {/* Filters */}
        <section className="mb-6 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {CATS.map((c) => (
              <button
                key={c}
                data-testid={`playshub-cat-${c}`}
                onClick={() => setCat(c)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest border transition-colors ${
                  cat === c ? 'bg-gold text-navy-deep border-gold' : 'border-gold/30 text-slate-300 hover:border-gold/60'
                }`}
              >
                {c === 'all' ? t('ph_filter_all') : c}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{t('ph_sort_label')}</span>
            <select
              data-testid="playshub-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-navy-deep border border-gold/30 rounded-md px-2 py-1.5 text-xs font-mono uppercase tracking-widest"
            >
              {SORTS.map((s) => <option key={s} value={s}>{t(`ph_sort_${s}`)}</option>)}
            </select>
          </div>
        </section>

        {/* Empty state */}
        {!loading && plays.length === 0 && (
          <div data-testid="playshub-empty" className="rounded-xl border border-dashed border-gold/30 p-10 text-center text-slate-400">
            <Sparkles className="w-6 h-6 mx-auto mb-2 text-gold/60" />
            {t('ph_empty_state')}
          </div>
        )}

        {/* Grid */}
        <section data-testid="playshub-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {plays.map((p) => <PlayCard key={p.id} play={p} lang={lang} />)}
        </section>

        {/* Load more */}
        {plays.length < total && (
          <div className="mt-8 flex justify-center">
            <button
              data-testid="playshub-load-more"
              onClick={() => fetchPlays(false)}
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-gold/40 text-gold font-mono uppercase tracking-widest text-xs hover:bg-gold/10 disabled:opacity-50"
            >
              <ChevronDown className="w-4 h-4" /> {loading ? t('ph_loading_more') : t('ph_load_more')}
            </button>
          </div>
        )}

        <footer className="mt-16 pt-8 border-t border-white/10 text-center text-[11px] font-mono uppercase tracking-widest text-slate-500">
          © {new Date().getFullYear()} PlayMaster IA · <Link to="/privacy" className="hover:text-gold">{t('ph_footer_privacy')}</Link> · <Link to="/terms" className="hover:text-gold">{t('ph_footer_terms')}</Link>
        </footer>
      </main>
    </div>
  );
}
